import { useTheme } from "@mui/material";
import { MessageCircle, Sparkles, Target } from "lucide-react";
import type { FC } from "react";
import earlyFreedomImg from "shared/assets/inspiration/ins-022-early-freedom.jpg";
import { RentDrawdownMode } from "web_myplann/Goals/Types/RentDrawdownMode";
import { InspirationTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { EarlyFreedomColdIntro } from "web_myplann/Inspiration/Components/EarlyFreedomColdIntro";
import { EarlyFreedomIncomeSheet } from "web_myplann/Inspiration/Components/EarlyFreedomIncomeSheet";
import { EarlyFreedomQuickSheet } from "web_myplann/Inspiration/Components/EarlyFreedomQuickSheet";
import { EarlyFreedomResult } from "web_myplann/Inspiration/Components/EarlyFreedomResult";
import { InspirationDetailLayout } from "web_myplann/Inspiration/Components/InspirationDetailLayout";
import { useEarlyFreedomJourney } from "web_myplann/Inspiration/Hooks/useEarlyFreedomJourney";
import { useOpenGoalFormFromInspiration } from "web_myplann/Inspiration/Hooks/useOpenGoalFormFromInspiration";
import {
	buildEarlyFreedomGoalPreset,
	EARLY_FREEDOM_GOAL_CATEGORY,
} from "web_myplann/Inspiration/Utils/earlyFreedomMath";
import { useContactAdvisor } from "web_myplann/Shared/Hooks/useContactAdvisor";

const PageResources = Resources.Inspiration.EarlyFreedom;

export const EarlyFreedomInspirationPage: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const { openGoalForm } = useOpenGoalFormFromInspiration();
	const { openChat } = useContactAdvisor();
	const {
		context,
		activeSheet,
		openSheet,
		closeSheet,
		submitQuick,
		submitIncome,
		setTargetExpenses,
		setMonthlySaving,
	} = useEarlyFreedomJourney();

	const isCold = context.level === "cold";

	const contactAdvisor = () => openChat(t(PageResources.advisorMessage));

	const handleCreateGoal = () => {
		const preset = buildEarlyFreedomGoalPreset(context);
		openGoalForm(
			{
				category: EARLY_FREEDOM_GOAL_CATEGORY,
				name: t(PageResources.title),
				monthlyRent: preset.monthlyRent,
				drawdownMode: RentDrawdownMode.Perpetual,
				dueDate: preset.dueDate,
			},
			InspirationTypeEnum.EarlyFreedom,
		);
	};

	return (
		<>
			<InspirationDetailLayout
				scrollResetKey={isCold}
				image={earlyFreedomImg}
				category={t(PageResources.category)}
				title={t(PageResources.title)}
				subtitle={t(PageResources.subtitle)}
				primaryCta={
					isCold
						? {
								label: t(PageResources.cta),
								icon: <Sparkles size={theme.iconSize.md} />,
								onClick: () => openSheet("quick"),
							}
						: {
								label: t(PageResources.createGoal),
								icon: <Target size={theme.iconSize.md} />,
								onClick: handleCreateGoal,
							}
				}
				secondaryCta={
					isCold
						? undefined
						: {
								label: t(PageResources.discussAdvisor),
								icon: <MessageCircle size={theme.iconSize.md} />,
								variant: "tertiary",
								onClick: contactAdvisor,
							}
				}
			>
				{isCold ? (
					<EarlyFreedomColdIntro />
				) : (
					<EarlyFreedomResult
						context={context}
						onChangeTarget={setTargetExpenses}
						onChangeSaving={setMonthlySaving}
						onEditIncome={() => openSheet("income")}
						onContact={contactAdvisor}
					/>
				)}
			</InspirationDetailLayout>

			<EarlyFreedomQuickSheet
				open={activeSheet === "quick"}
				onClose={closeSheet}
				onSubmit={submitQuick}
			/>

			<EarlyFreedomIncomeSheet
				open={activeSheet === "income"}
				initial={context.rentNet}
				onClose={closeSheet}
				onSubmit={submitIncome}
			/>
		</>
	);
};
